/**
 * Pending Approval Page
 *
 * Feature: 001-frontend-integration
 * Task: T030
 * Description: Displays when authenticated user has no role assigned
 * Constitution: v1.11.0
 * Author: Peter Dobler
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../modules/backup/hooks/useAuth';
import { apiClient } from '../../services/apiClient';

/** What the last check found: the server has no role for the user yet, or the check itself failed. */
interface CheckResult {
  outcome: 'pending' | 'failed';
  at: Date;
}

const checkedAt = new Intl.DateTimeFormat(undefined, { timeStyle: 'short' });

export const PendingApproval: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const [checkingRole, setCheckingRole] = useState(false);
  const [lastCheck, setLastCheck] = useState<CheckResult | null>(null);

  // Ask the server whether a role has been assigned. The auth middleware refuses a user without a role with 403
  // ROLE_NOT_ASSIGNED and answers 200 with the role once one is set. useAuth reads the role once per sign-in, so a
  // full load into the app is what picks a new role up.
  const checkAccess = useCallback(async () => {
    setCheckingRole(true);
    try {
      const response = await apiClient.get<{ user?: { role?: string | null } }>('/api/auth/session');
      if (response?.data?.user?.role) {
        window.location.assign('/dashboard');
        return;
      }
      setLastCheck({ outcome: 'pending', at: new Date() });
    } catch (error) {
      const code = (error as { response?: { data?: { code?: string } } }).response?.data?.code;
      setLastCheck({ outcome: code === 'ROLE_NOT_ASSIGNED' ? 'pending' : 'failed', at: new Date() });
    } finally {
      setCheckingRole(false);
    }
  }, []);

  // Check on load, then every minute.
  useEffect(() => {
    void checkAccess();
    const checkRoleInterval = setInterval(() => void checkAccess(), 60000);
    return () => clearInterval(checkRoleInterval);
  }, [checkAccess]);

  // If somehow authenticated with a role, redirect
  useEffect(() => {
    if (isAuthenticated && user?.role) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleRefreshStatus = () => {
    void checkAccess();
  };

  return (
    <div className='min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50'>
      <div className='bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-md w-full mx-4 text-center'>
        {/* Status Icon */}
        <div className='mb-6'>
          <div className='w-20 h-20 mx-auto bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center'>
            <svg
              className='w-10 h-10 text-yellow-500'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
              />
            </svg>
          </div>
        </div>

        {/* Title */}
        <h1 className='text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2'>Access Pending Approval</h1>

        {/* User Info */}
        {user?.email && (
          <p className='text-gray-500 dark:text-gray-400 text-sm mb-4'>
            Signed in as: <span className='font-medium'>{user.email}</span>
          </p>
        )}

        {/* Message */}
        <p className='text-gray-600 dark:text-gray-400 mb-6'>
          Your account has been created, but you don’t have a role assigned yet. Please contact
          your system administrator to request access.
        </p>

        {/* Instructions */}
        <div className='bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mb-6 text-left'>
          <h3 className='font-medium text-blue-800 dark:text-blue-300 mb-2'>What to do next:</h3>
          <ol className='text-sm text-blue-700 dark:text-blue-400 space-y-2 list-decimal list-inside'>
            <li>Contact your administrator to request a role assignment</li>
            <li>Provide them with your email address shown above</li>
            <li>Once approved, click “Refresh Status” below</li>
          </ol>
        </div>

        {/* Action Buttons */}
        <div className='space-y-3'>
          <button
            onClick={handleRefreshStatus}
            disabled={checkingRole}
            className={`w-full px-4 py-3 rounded-lg font-medium transition-colors ${
              checkingRole
                ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {checkingRole ? (
              <span className='flex items-center justify-center gap-2'>
                <svg className='animate-spin h-5 w-5' viewBox='0 0 24 24'>
                  <circle
                    className='opacity-25'
                    cx='12'
                    cy='12'
                    r='10'
                    stroke='currentColor'
                    strokeWidth='4'
                    fill='none'
                  />
                  <path
                    className='opacity-75'
                    fill='currentColor'
                    d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                  />
                </svg>
                Checking...
              </span>
            ) : (
              'Refresh Status'
            )}
          </button>

          <button
            onClick={handleLogout}
            className='w-full px-4 py-3 rounded-lg font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors'
          >
            Sign Out
          </button>
        </div>

        {/* The last check's result */}
        {lastCheck && (
          <p role='status' className='text-sm text-gray-600 dark:text-gray-400 mt-4'>
            {lastCheck.outcome === 'pending'
              ? `Still waiting for approval (checked at ${checkedAt.format(lastCheck.at)}).`
              : `Could not check your access at ${checkedAt.format(lastCheck.at)}. It will try again in a minute, or use Refresh Status.`}
          </p>
        )}

        {/* Help Text */}
        <p className='text-xs text-gray-400 dark:text-gray-500 mt-6'>
          Your status will be checked automatically every minute.
        </p>
      </div>
    </div>
  );
};

export default PendingApproval;
